<template>
  <div class="flex">
    <div v-if="!current_metric" class="flex flex-col w-full">
      <!-- Search and Filter Bar -->
      <div class="flex mb-6 justify-end gap-2">
        <TextInput
          v-model="metric_filter"
          :validate-on-input="true"
          name="metric_filter"
          :placeholder="$t('Search metrics...')"
          class="w-full max-w-md" />
        <ComboBoxInput
          v-model:model-value="selected_object_type"
          :items="utils.SortedObjectTypes()"
          name="object_type"
          classes="relative dark:text-white w-96"
          label-attr="label"
          value-attr="type"
          :is-box-background="true"
          :label="$t('Object Type')"
          :tooltip-info="$t('Select object type here')"
          :show-label="false"
          :floating-label="true"
          :allow-null="false" />
      </div>

      <!-- Metrics Grid -->
      <div
        v-if="filtered_metrics.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[calc(100vh-300px)] overflow-y-auto relative scroll-container">
        <div
          v-for="metric in filtered_metrics"
          :key="metric.id"
          :tabindex="1"
          class="bg-white dark:bg-dark-box-background border border-gray-200 dark:border-dark-divider-border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
          @keyup.enter="editMetricBtnHandler(metric)">
          <!-- Metric Header -->
          <div class="p-4">
            <div class="flex items-start space-x-3">
              <!-- Icon -->
              <div class="flex-shrink-0">
                <component :is="getMetricIcon(metric.input_type)" class="w-10 h-10 text-gray-500 dark:text-gray-400" />
              </div>
              <!-- Title and Type -->
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {{ metric.input_type }}
                </h3>
                <p class="mt-1 text-base font-medium text-gray-900 dark:text-white truncate" :title="metric.name">
                  {{ metric.name }}
                </p>
              </div>

              <!-- Indicators -->
              <div class="flex items-center space-x-1">
                <KeyRound v-if="metric.metric_colors?.ranges?.length" color="#0002E1" class="w-4 h-4 text-gray-400" />
                <Palette v-if="metric.metric_colors.ranges" color="#2AC986" class="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="border-t border-gray-200 dark:border-dark-divider-border">
            <div class="grid grid-cols-3 divide-x divide-gray-200 dark:divide-dark-divider-border">
              <!-- Edit -->
              <button
                class="p-3 text-sm text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-dark-hover-background transition-colors duration-200 flex items-center justify-center"
                @click="editMetricBtnHandler(metric)">
                <Pencil class="w-4 h-4 mr-2" />
                <span>{{ $t("Edit") }}</span>
              </button>

              <!-- Duplicate -->
              <button
                class="p-3 text-sm text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-gray-50 dark:hover:bg-dark-hover-background transition-colors duration-200 flex items-center justify-center"
                @click="duplicateMetric(metric)">
                <Copy class="w-4 h-4 mr-2" />
                <span>{{ $t("Duplicate") }}</span>
              </button>

              <!-- Delete -->
              <button
                class="p-3 text-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-50 dark:hover:bg-dark-hover-background transition-colors duration-200 flex items-center justify-center"
                @click="showConfirmDelete(metric)">
                <Trash2 class="w-4 h-4 mr-2" />
                <span>{{ $t("Delete") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Empty State -->
      <div v-else class="w-full bg-white dark:bg-dark-box-background rounded-lg shadow-sm p-8 text-center">
        <div class="flex flex-col items-center">
          <Info class="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {{ $t("No") }} {{ label_plural }} {{ $t("found") }}
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {{ $t("Get started by creating a new") }} {{ label.toLowerCase() }}
          </p>
          <button
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            @click="createNewMetricBtnHandler">
            <Plus class="w-5 h-5 mr-2" />
            <span>{{ $t("Create New") }} {{ label }}</span>
          </button>
        </div>
      </div>

      <!-- Scroll Shadow -->
      <div
        v-if="hasMoreContent"
        class="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-dark-box-background to-transparent pointer-events-none" />
    </div>
    <div v-else class="flex w-full">
      <MetricForm
        :metric="current_metric"
        @update="(metric) => (current_metric = metric)"
        @done="current_metric = null"
        @cancel="current_metric = null" />
    </div>
  </div>
</template>

<script>
import MetricForm from "@/views/dashboard/admin/metrics/MetricForm.vue"
import NumbersIcon from "@/components/shared/icons/NumbersIcon.vue"
import DecimalIcon from "@/components/shared/icons/DecimalIcon.vue"
import TextIcon from "@/components/shared/icons/TextIcon.vue"
import TextareaIcon from "@/components/shared/icons/TextareaIcon.vue"
import SelectListIcon from "@/components/shared/icons/SelectListIcon.vue"
import DateIcon from "@/components/shared/icons/DateIcon.vue"
import DateTimeIcon from "@/components/shared/icons/DateTimeIcon.vue"
import TimeIcon from "@/components/shared/icons/TimeIcon.vue"
import CheckboxIcon from "@/components/shared/icons/CheckboxIcon.vue"
import UrlIcon from "@/components/shared/icons/UrlIcon.vue"
import ChecklistIcon from "@/components/shared/icons/ChecklistIcon.vue"
import OrderedListIcon from "@/components/shared/icons/OrderedListIcon.vue"
import BulletPointsListIcon from "@/components/shared/icons/BulletPointsListIcon.vue"
import ObjectReferenceIcon from "@/components/shared/icons/ObjectReferenceIcon.vue"
import PercentIcon from "@/components/shared/icons/PercentIcon.vue"
import CurrencyIcon from "@/components/shared/icons/CurrencyIcon.vue"
import StarRatingIcon from "@/components/shared/icons/StarRatingIcon.vue"
import BarRatingIcon from "@/components/shared/icons/BarRatingIcon.vue"
import EmailColoredIcon from "@/components/shared/icons/EmailColoredIcon.vue"
import { Palette, Plus, Trash2, Pencil, Copy, Info, KeyRound } from "lucide-vue-next"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import TextInput from "@/components/shared/TextInput.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"

export default {
  components: {
    MetricForm,
    ComboBoxInput,
    TextInput,
    NumbersIcon,
    DecimalIcon,
    TextIcon,
    TextareaIcon,
    SelectListIcon,
    DateIcon,
    DateTimeIcon,
    TimeIcon,
    CheckboxIcon,
    UrlIcon,
    ChecklistIcon,
    OrderedListIcon,
    BulletPointsListIcon,
    ObjectReferenceIcon,
    PercentIcon,
    CurrencyIcon,
    StarRatingIcon,
    BarRatingIcon,
    EmailColoredIcon,
    KeyRound,
    Palette,
    Plus,
    Pencil,
    Trash2,
    Info,
    Copy
  },
  inject: ["removeMetricsFromView"],
  props: {
    metric: {
      type: Object,
      required: false
    },
    metrics: {
      type: Array,
      required: true
    },
    metricFilter: {
      type: String,
      required: true
    },
    sortOption: {
      type: String,
      required: true
    },
    metricClass: {
      type: String,
      required: true
    },
    objectType: {
      type: String,
      required: false
    },
    dataSource: {
      type: String,
      required: false
    }
  },
  emits: ["editMetric", "deleteMetric", "newMetric", "update"],
  data() {
    return {
      utils,
      current_metric: null,
      cloned_metrics: [],
      has_data_sources: "all",
      selected_object_type: "company",
      metric_filter: null,
      hasMoreContent: false
    }
  },
  computed: {
    filtered_metrics() {
      return this.all_metrics
        .filter(
          (m) =>
            (!this.metric_filter ||
              (this.metric_filter &&
                m.name.toLowerCase().search(this.metric_filter.toLowerCase().replaceAll("(", "").replaceAll(")", "")) != -1)) &&
            (!this.selected_object_type || (this.selected_object_type && m.object_type == this.selected_object_type))
        )
        .sort((a, b) => {
          if (this.sortOption === "datetime_created") {
            return new Date(b.datetime_created) - new Date(a.datetime_created)
          } else if (this.sortOption === "last_edited") {
            return new Date(b.last_edited) - new Date(a.last_edited)
          } else if (this.sortOption === "name") {
            return a.name.localeCompare(b.name)
          }
          return 0
        })
    },
    all_metrics() {
      return this.cloned_metrics
        .filter((m) => m.metric_class == this.metricClass)
        .filter((m) => (this.selected_object_type && m.object_type == this.selected_object_type) || true)
    },
    label() {
      return this.metricClass == "computed_metric" ? "Calculated Metric" : this.metricClass == "custom_metric" ? "Custom Metric" : "Metric"
    },
    label_plural() {
      return this.metricClass == "computed_metric"
        ? "Calculated Metrics"
        : this.metricClass == "custom_metric"
          ? "Custom Metrics"
          : "Metrics"
    }
  },
  watch: {
    dataSource() {
      this.has_data_sources = this.dataSource
    },
    metricFilter: {
      handler() {
        this.metric_filter = this.metricFilter
      },
      immediate: true
    },
    metrics() {
      this.cloned_metrics = this.$utils.clone(this.metrics)
    },
    metric() {
      this.current_metric = this.metric
    },
    objectType() {
      this.selected_object_type = this.objectType
      this.$emit("update", this.selected_object_type)
    },
    selected_object_type() {
      this.$emit("update", this.selected_object_type)
    }
  },
  mounted() {
    this.$refs?.search_input?.focus()
    this.setupScrollListener()
    this.selected_object_type = this.objectType
    if (this.$route.query["o"]) {
      if (
        utils
          .SortedObjectTypes()
          .map((x) => utils.slugify(x.type))
          .indexOf(this.$route.query["o"]) != -1
      ) {
        this.selected_object_type = this.$route.query["o"]
      } else {
        this.selected_object_type = utils.SortedObjectTypes()[0].type
      }
    }
  },

  deactivated() {
    this.unregister_listeners()
    this.removeScrollListener()
  },
  created() {
    this.cloned_metrics = this.$utils.clone(this.metrics)
    this.register_listeners()
    if (this.$route.query?.redirect_url) {
      this.$emit("newMetric")
    }
  },
  methods: {
    showConfirmDelete(metric_to_be_deleted) {
      this.$emitter.emit(ALL_EMITTERS.GENERIC_DELETE_MODAL, {
        title: this.$t("Delete Metric"),
        success_message: this.$t("Metric") + ` ${metric_to_be_deleted.name} ` + this.$t("deleted successfully"),
        message: this.$t("Do you want to delete this metric?"),
        action: () => {
          this.deleteMetric(metric_to_be_deleted)
        }
      })
    },

    deleteMetric(metric) {
      this.$store
        .dispatch("objects/deleteMetric", metric)
        .then((res) => {
          if (res?.data?.status == "METRIC_USED") {
            /*this.$toast.open({
              message: this.$t("This metric is not allowed to be deleted, it's being used by other metrics"),
              position: "top",
              type: "error",
              duration: 6000
            })*/
          } else {
            this.$emitter.emit(ALL_EMITTERS.METRIC_DELETED, metric)
            this.removeMetricsFromView([metric.reference_name])
            this.$store.dispatch("data/getGraphs")
          }
        })
        .catch(() => {
          this.$toast.open({
            message: this.$t("Error deleting metric"),
            position: "top",
            type: "error",
            duration: 3000
          })
        })
    },
    getMetricIcon(inputType) {
      const iconMap = {
        Number: NumbersIcon,
        Text: TextIcon,
        Textarea: TextareaIcon,
        RichTextarea: TextareaIcon,
        Select: SelectListIcon,
        Date: DateIcon,
        DateTime: DateTimeIcon,
        Time: TimeIcon,
        Checkbox: CheckboxIcon,
        URL: UrlIcon,
        Checklist: ChecklistIcon,
        OrderedList: OrderedListIcon,
        BulletPointsList: BulletPointsListIcon,
        objectReference: ObjectReferenceIcon,
        Decimal: DecimalIcon,
        Percent: PercentIcon,
        Currency: CurrencyIcon,
        StarRating: StarRatingIcon,
        BarRating: BarRatingIcon,
        Email: EmailColoredIcon
      }
      return iconMap[inputType] || TextIcon
    },

    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.METRIC_DELETED, (metric) => {
        let index = this.cloned_metrics.findIndex((m) => m.id == metric.id)
        if (index != -1) {
          this.cloned_metrics.splice(index, 1)
        }
      })
      this.$emitter.on(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS, (metric) => {
        let index = this.cloned_metrics.findIndex((m) => m.id == metric.id)
        if (index != -1) {
          this.cloned_metrics[index] = metric
        }
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.METRIC_DELETED)
      this.$emitter.off(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS)
    },
    createNewMetricBtnHandler() {
      if (this.metricClass == "computed_metric") {
        this.$emit("newMetric")
      } else {
        this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, { view_type: "table" })
      }
    },
    editMetricBtnHandler(metric) {
      this.current_metric = metric
    },
    setupScrollListener() {
      const container = document.querySelector(".scroll-container")
      if (container) {
        container.addEventListener("scroll", this.checkScroll)
        // Initial check
        this.checkScroll({ target: container })
      }
    },

    removeScrollListener() {
      const container = document.querySelector(".scroll-container")
      if (container) {
        container.removeEventListener("scroll", this.checkScroll)
      }
    },

    checkScroll(event) {
      const { scrollHeight, scrollTop, clientHeight } = event.target
      this.hasMoreContent = scrollHeight > clientHeight && scrollHeight - scrollTop - clientHeight > 1
    },

    duplicateMetric(metric) {
      let metric_clone = utils.clone(metric)
      delete metric_clone["id"]
      delete metric_clone["created_by"]
      delete metric_clone["reference_name"]
      let new_name = ""
      if (!metric_clone["name"].includes("(Copy#")) {
        new_name = metric_clone["name"] + " (Copy#1)"
      } else {
        new_name = metric_clone["name"].replace(/\(Copy#(\d+)\)/g, (match, g1) => `(Copy#${Number(g1) + 1})`)
      }
      metric_clone["name"] = new_name
      this.$store
        .dispatch("objects/addMetric", metric_clone)
        .then((res) => {
          this.$emitter.emit(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_OVERVIEW_COMPONENT, res)
          this.$emitter.emit(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS, res)
          this.$toast.open({
            message: this.$t("Metric") + ` ${metric.name} ` + this.$t("duplicated successfully"),
            position: "top",
            type: "success",
            duration: 3000
          })
        })
        .catch((err) => {
          this.$toast.open({
            message: this.$t(`Error duplicating metric: ${err.data?.message}`),
            position: "top",
            type: "error",
            duration: 3000
          })
        })
    }
  }
}
</script>
